import React, { useCallback } from 'react';
import { PencilSimpleIcon, TrashIcon } from '@phosphor-icons/react';
import { Button } from '../../../../Utility/Button';
import { PaginationListType } from '../../../../../Types/PaginationParams';
import useOpenApiConfigs from '../../../../../Hooks/useOpenApiConfigs';
import { OpenApiConfig } from '../../../../../Types';
import AdminViewContent from '../../../../Utility/AdminViewContent';
import AdminViewHeader from '../../../../Utility/AdminViewHeader';
import { openApiService } from '../../../../../Services/openApiService';
import { RootState, useAppSelector } from '../../../../../store';
import PaginatedView from '../../../../Utility/PaginatedView';
import { getBreakpoint } from '../../../../../utils/breakpoint';

const OpenApiConfigList: React.FC = () => {
    const settings = useAppSelector((state: RootState) => state.settings);
    const { handleOpenConfirmDialog, i18n, windowWidth, handleEdit, handleCreateNew } = useOpenApiConfigs();

    const getConfigsWrapper = useCallback(
        (page: number, itemsPerPage: number, optionalParams?: Record<string, string>, query?: string | null) => {
            return openApiService.getConfigs(settings.demo, page, itemsPerPage, optionalParams, query);
        },
        [settings.demo],
    );

    const GRID_COLS =
        getBreakpoint('lg') > windowWidth
            ? 'grid-cols-[minmax(150px,300px),minmax(150px,300px),minmax(180px,1fr)]'
            : 'grid-cols-[minmax(150px,300px),minmax(150px,300px),minmax(150px,250px),minmax(150px,250px),minmax(190px,1fr)]';

    return (
        <div className="">
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center mb-4">
                <div>
                    <h2 className="text-xl font-semibold text-layout-textcolor">
                        {i18n.translate({ id: 'configs.title', sourceName: 'OpenAPI' })}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        {i18n.translate({ id: 'configs.subtitle', sourceName: 'OpenAPI' })}
                    </p>
                </div>
            </div>

            <PaginatedView
                listType={PaginationListType.OpenApiConfigs}
                itemsFunction={getConfigsWrapper}
                itemsPerPage={10}
                renderItem={(config: OpenApiConfig, index: number) => (
                    <AdminViewContent
                        key={config.identifier}
                        mobile={windowWidth < getBreakpoint('md')}
                        identifier={config.identifier}
                        gridCols={GRID_COLS}
                        cols={{
                            optionalCols: [
                                {
                                    content: (
                                        <div className="flex flex-col">
                                            <div>{config.title}</div>
                                            <div className="text-sm text-gray-500 line-clamp-3 overflow-hidden whitespace-normal break-words">
                                                {config.description}
                                            </div>
                                        </div>
                                    ),
                                },
                                {
                                    content: <span>{config.baseUrl}</span>,
                                    hideCol: getBreakpoint('md') > windowWidth,
                                },
                                {
                                    content: <span>{config.authType}</span>,
                                    hideCol: getBreakpoint('lg') > windowWidth,
                                },
                                {
                                    content: (
                                        <div className="text-center">
                                            <span>{config.endpointCount || 0}</span>
                                        </div>
                                    ),
                                    hideCol: getBreakpoint('lg') > windowWidth,
                                },
                            ],
                            button: {
                                buttons: [
                                    {
                                        variant: !settings.demo ? 'primaryRound' : 'disabledRound',
                                        onClick: () => {
                                            if (!settings.demo) {
                                                handleEdit(config);
                                            }
                                        },
                                        icon: <PencilSimpleIcon />,
                                        tooltipText: i18n.translate({
                                            id: 'configs.edit',
                                            sourceName: 'OpenAPI',
                                        }),
                                    },
                                    {
                                        variant: !settings.demo ? 'secondaryRound' : 'disabledRound',
                                        onClick: () => {
                                            if (!settings.demo) {
                                                handleOpenConfirmDialog(config.identifier, config.title);
                                            }
                                        },
                                        icon: <TrashIcon />,
                                        tooltipText: i18n.translate({
                                            id: 'configs.delete',
                                            sourceName: 'OpenAPI',
                                        }),
                                    },
                                ],
                            },
                        }}
                    />
                )}
            >
                {({ list, filter }) => (
                    <>
                        {filter}
                        {!settings.demo && (
                            <div className="mb-4 md:hidden">
                                <Button className="w-full mb-2" onClick={handleCreateNew}>
                                    {i18n.translate({ id: 'button', sourceName: 'Models' })}
                                </Button>
                            </div>
                        )}

                        <div className="hidden md:block">
                            <AdminViewHeader
                                gridCols={GRID_COLS}
                                cols={{
                                    cols: [
                                        {
                                            text: i18n.translate({ id: 'configs.table.title', sourceName: 'OpenAPI' }),
                                        },
                                        {
                                            text: i18n.translate({
                                                id: 'configs.table.baseUrl',
                                                sourceName: 'OpenAPI',
                                            }),
                                        },
                                        {
                                            text: i18n.translate({ id: 'configs.table.auth', sourceName: 'OpenAPI' }),
                                            hideCol: getBreakpoint('lg') > windowWidth,
                                        },
                                        {
                                            text: i18n.translate({
                                                id: 'configs.table.endpoints',
                                                sourceName: 'OpenAPI',
                                            }),
                                            hideCol: getBreakpoint('lg') > windowWidth,
                                        },
                                    ],
                                    ...(!settings.demo && {
                                        button: {
                                            text: i18n.translate({ id: 'configs.create', sourceName: 'OpenAPI' }),
                                            function: () => handleCreateNew(),
                                        },
                                    }),
                                }}
                            />
                            <hr className="border-layout-borderColor mt-5" />
                        </div>
                        {list}
                    </>
                )}
            </PaginatedView>
        </div>
    );
};

export default OpenApiConfigList;
